import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { MediaMatcher } from '@angular/cdk/layout';
import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TransitionService } from '@uirouter/angular';
import { RouteMetadataService } from 'src/app/app-common/services/route-metadata.service';
import { UserStateService } from 'src/app/auth/services/user-state.service';
import { TrackerService } from 'src/app/components/logging/tracker.service';
import { RvshareAppDetectionService } from 'src/app/screen-sharing/services/rvshare-app-detection.service';
import { LocalStorageHelperService } from 'src/app/shared/services/local-storage-helper.service';
import { LiveAnnouncerService } from '../../../accessibility/services/live-announcer/live-announcer.service';

export type SidenavState = 'expand' | 'collapse' | 'open' | 'closed';

@Component({
  selector: 'app',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  animations: [
    trigger('sidenavAnimation', [
      state('expand', style({ width: '261px' })),
      state('collapse', style({ width: '61px' })),
      state('open', style({ width: '261px' })),
      state('closed', style({ width: '61px' })),
      transition('expand <=> collapse', animate('0.15s ease-in-out')),
      transition('open => collapse', animate('0.15s ease-in-out')),
    ]),
    trigger('sidenavToggleAnimation', [
      state('expand', style({ transform: 'rotate(0deg)' })),
      state('collapse', style({ transform: 'rotate(180deg)' })),
      transition('expand <=> collapse', animate('0.15s ease-in-out')),
    ]),
    trigger('hideToggleAnimation', [
      state('expand', style({ opacity: 1 })),
      state('collapse', style({ opacity: 1 })),
      state('open', style({ opacity: 0 })),
      state('closed', style({ opacity: 0 })),
      transition('expand <=> closed', animate('0.15s ease-in-out')),
      transition('collapse <=> closed', animate('0.15s ease-in-out')),
      transition('open => expand', animate('0.15s ease-in-out')),
      transition('open => collapse', animate('0.15s ease-in-out')),
    ]),
    trigger('sidenavContentAnimation', [
      state('expand', style({ marginLeft: '261px' })),
      state('collapse', style({ marginLeft: '61px' })),
      state('open', style({ marginLeft: '0' })),
      state('closed', style({ marginLeft: '0' })),
      transition('expand <=> collapse', animate('0.15s ease-in-out')),
      transition('collapse <=> open', animate('0.15s ease-in-out')),
    ]),
  ],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  sidenavState: SidenavState = 'expand';
  toggleAnimationState: SidenavState = 'expand';
  prevDesktopState: 'expand' | 'collapse' = 'expand';
  isAnimating = false;
  currentRoute: string = '';
  companyMenuOpened = false;
  hideNavigation;
  announcement: string = '';

  public readonly isMobile = signal(true);
  private readonly _mobileQuery: MediaQueryList;
  private readonly _mobileQueryListener: () => void;

  @ViewChild('contentLayoutBody') contentLayoutBody: ElementRef<HTMLElement>;

  constructor(
    private media: MediaMatcher,
    private transitionService: TransitionService,
    private rvshareAppDetectionService: RvshareAppDetectionService,
    private routeMetadataService: RouteMetadataService,
    private userStateService: UserStateService,
    private trackerService: TrackerService,
    private localStorageHelperService: LocalStorageHelperService,
    private liveAnnouncerService: LiveAnnouncerService
  ) {
    if (this.rvshareAppDetectionService.isScreenShareApp()) {
      // Rvshare app is always mobile
      this.isMobile.set(true);
      this.sidenavState = 'closed';
    } else {
      // Otherwise, set up the mobile query listener
      this._mobileQuery = this.media.matchMedia('(max-width: 1023px)');
      this.isMobile.set(this._mobileQuery.matches);

      if (this._mobileQuery.matches) {
        this.sidenavState = 'closed';
      } else {
        const savedSidenavState =
          this.localStorageHelperService.get('sidenavState');
        if (
          savedSidenavState === 'expand' ||
          savedSidenavState === 'collapse'
        ) {
          this.sidenavState = savedSidenavState;
          this.toggleAnimationState = savedSidenavState;
          this.prevDesktopState = savedSidenavState;
        }
      }

      this._mobileQueryListener = () => {
        this.isMobile.set(this._mobileQuery.matches);
        if (this._mobileQuery.matches) {
          // Store desktop state before switching to mobile
          if (
            this.sidenavState === 'expand' ||
            this.sidenavState === 'collapse'
          ) {
            this.prevDesktopState = this.sidenavState;
          }
          this.sidenavState = this.companyMenuOpened ? 'open' : 'closed';
        } else {
          this.sidenavState = this.prevDesktopState;
        }
      };
      this._mobileQuery.addEventListener('change', this._mobileQueryListener);
    }
    this.liveAnnouncerService
      .getAnnouncement()
      .pipe(takeUntilDestroyed())
      .subscribe((announcement) => {
        this.announcement = announcement;
      });
  }

  ngOnInit() {
    this.transitionService.onSuccess({}, (transition) => {
      const toState = transition.to().name;
      this.hideNavigation =
        toState.indexOf('apps.auth') !== -1 ||
        toState.indexOf('apps.screen-sharing.home') !== -1 ||
        toState.indexOf('apps.billing.invoice') !== -1 ||
        toState.indexOf('apps.billing.unpaid') !== -1;
      if (this.hideNavigation || this.isMobile()) {
        this.sidenavState = 'closed';
      } else if (!this.isMobile()) {
        this.sidenavState = this.prevDesktopState;
      }
      this.currentRoute = toState;
      this.updateTitle();
    });
  }

  ngOnDestroy() {
    if (!this.rvshareAppDetectionService.isScreenShareApp()) {
      this._mobileQuery.removeEventListener(
        'change',
        this._mobileQueryListener
      );
    }
  }

  toggleSidenav() {
    this.sidenavState = this.sidenavState === 'expand' ? 'collapse' : 'expand';
    this.prevDesktopState = this.sidenavState;
    this.localStorageHelperService.set('sidenavState', this.sidenavState);

    this.trackerService.userEvent(
      'Toggle Sidebar',
      this.userStateService.getUsername(),
      true,
      {
        toState: this.sidenavState,
      }
    );
  }

  openMobileMenu() {
    if (this.isMobile()) {
      this.sidenavState = 'open';
    }
  }

  onOpenedChange(opened: boolean) {
    if (this.isMobile()) {
      this.sidenavState = opened ? 'open' : 'closed';
    }
  }

  onAnimationStart(event: any) {
    this.isAnimating = true;
    // Only trigger toggle animation for expand/collapse transitions
    if (event.toState === 'expand' || event.toState === 'collapse') {
      this.toggleAnimationState = event.toState;
    }
  }

  onAnimationDone(event: any) {
    this.isAnimating = false;
  }

  updateTitle(): void {
    const routeMetadata = this.routeMetadataService.getActiveRouteMetadata();
    const titleText = routeMetadata ? `${routeMetadata.title} | ` : '';

    document.title = titleText + 'Rise Vision Apps';
  }

  onHeaderResize(event: { width: number; height: number }) {
    if (this.contentLayoutBody) {
      this.contentLayoutBody.nativeElement.style.setProperty(
        '--common-header-height',
        event.height + 'px'
      );
    }
  }

  onSkipToContent(event: Event) {
    event.preventDefault();
    this.contentLayoutBody?.nativeElement?.focus();
  }
}
